import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import RootStoreContext from '~/contexts/RootStoreContext';
import { actions as modalActions } from '~/domains/modal';
import { selectors as agentScannersSelectors } from '~/domains/agentScanners';
import {
  actions as commonActions,
  selectors as commonSelectors
} from '~/domains/common';
import { selectors as featureFlagsSelectors } from '~/domains/featureFlags';
import { SHOW_AGENTFORCE_IMPORT_BUTTON } from '~/domains/featureFlags/keys';
import { getActiveOrganizationId } from '~/utils/organization';
import { escapeHtml } from '../../utils/utils';
import DeleteConfirmModal from '../ScanConfigurationActions/DeleteConfirmModal';
import ScanConfigurationsListComponent from './ScanConfigurationsList';

/**
 * Container for ScanConfigurationsList component
 * Connects to Redux store following the same pattern as other containers
 */
const mapStateToProps = (state, ownProps) => {
  const profile = commonSelectors.profile(state);
  const activeOrganizationId = getActiveOrganizationId(profile);

  return {
    // Use filtered data from props if available, otherwise use Redux data
    scanConfigurations:
      ownProps.scanConfigurations || agentScannersSelectors.scanners(state),
    isFetchingScanConfigurations:
      agentScannersSelectors.isFetchingScanners(state),
    scannersError: agentScannersSelectors.scannersError(state),
    organizationId: activeOrganizationId,
    alert: commonSelectors.alert(state),
    showAgentforceImportButton: featureFlagsSelectors.featureFlag(
      state,
      SHOW_AGENTFORCE_IMPORT_BUTTON
    )
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  // Import actions dynamically to avoid circular dependencies
  // eslint-disable-next-line global-require
  const { actions: agentScannersActions } = require('~/domains/agentScanners');

  return {
    // Action creators
    onCloseAlert: () => dispatch(commonActions.closeAlert()),
    openModal: ({ modalType, modalProps }) =>
      dispatch(modalActions.openModal({ modalType, modalProps })),
    onLoadScanConfigurations: (organizationId) =>
      dispatch(agentScannersActions.loadAgentScanners(organizationId)),

    // Use the handler from parent props, or fallback to no-op
    onScanConfigurationClick:
      ownProps.onScanConfigurationClick ||
      (() => {
        // Fallback handler - no action needed
      }),

    /**
     * Triggers immediate scan execution and shows alert with abort option.
     * @param {string} organizationId - The organization ID
     * @param {Object} scanConfig - Scanner configuration from the list (passed by ScanConfigurationsList component)
     * @param {string} scanConfig.id - Scanner ID for execution
     * @param {string} [scanConfig.name] - Scanner name (validated with optional chaining + fallback)
     */
    onRunNowClick: (organizationId, scanConfig) => {
      // Trigger the scan execution via the agentScanners action
      dispatch(
        agentScannersActions.runScanConfiguration(organizationId, scanConfig.id)
      ).then(() => {
        dispatch(agentScannersActions.loadAgentScanners(organizationId)).then(
          () => {
            // Create a unique key for this scan's abort function
            // Replace hyphens with underscores to make it a valid JavaScript identifier
            const abortFunctionKey = `abortScan_${scanConfig.id.replace(/-/g, '_')}`;

            const cleanup = () => {
              if (window[abortFunctionKey]) {
                delete window[abortFunctionKey];
              }
            };

            const handleAbort = () => {
              // Prevent multiple clicks
              if (!window[abortFunctionKey]) {
                return;
              }

              cleanup();

              // Call the abort action
              dispatch(
                agentScannersActions.abort(organizationId, scanConfig.id)
              ).then(() => {
                // Close the current alert
                dispatch(commonActions.closeAlert());

                // Show abort confirmation alert (no auto-close)
                // isHTML: false - no HTML needed, safe by default
                const abortAlert = {
                  type: 'info',
                  message: `Your '${scanConfig?.name || 'Unknown'}' scan is being aborted.`,
                  isHTML: false
                };

                dispatch(commonActions.setAlert(abortAlert));

                // Reload the list to show updated status
                if (organizationId) {
                  dispatch(
                    agentScannersActions.loadAgentScanners(organizationId)
                  );
                }
              });
            };

            // Store function globally so HTML onclick can access it
            window[abortFunctionKey] = handleAbort;

            // Use escapeHtml to prevent XSS from user-provided scanner names
            // Also escape abortFunctionKey for defense in depth (even though it's derived from scanConfig.id)
            const safeAbortKey = escapeHtml(abortFunctionKey);
            const runAlert = {
              type: 'info',
              message: `Your '${escapeHtml(scanConfig?.name || 'Unknown')}' Agent Scanner is currently running. Click <a href="javascript:void(0)" onclick="if(window['${safeAbortKey}']){window['${safeAbortKey}']();}" style="color:inherit;text-decoration:underline;cursor:pointer;font-weight:600;">Abort</a> if you need to stop it now.`,
              isHTML: true
            };

            dispatch(commonActions.setAlert(runAlert));

            // Note: Alert will remain visible until user manually closes it
            // Clean up abort function when alert is closed
            // The cleanup will happen when the component unmounts or user closes the alert
          }
        );
      });
    },

    /**
     * Aborts a running scan and shows confirmation alert.
     * @param {string} organizationId - The organization ID
     * @param {Object} scanConfig - Scanner configuration from the list (passed by ScanConfigurationsList component)
     * @param {string} scanConfig.id - Scanner ID to abort
     * @param {string} [scanConfig.name] - Scanner name (validated with optional chaining + fallback)
     */
    onAbortClick: (organizationId, scanConfig) => {
      // Trigger the abort scan action
      dispatch(agentScannersActions.abort(organizationId, scanConfig.id)).then(
        () => {
          // isHTML: false - no HTML needed, safe by default
          const abortAlert = {
            type: 'info',
            message: `Your '${scanConfig?.name || 'Unknown'}' scan is being aborted.`,
            isHTML: false
          };

          dispatch(commonActions.setAlert(abortAlert));

          // Reload the list to show updated status
          if (organizationId) {
            dispatch(agentScannersActions.loadAgentScanners(organizationId));
          }
        }
      );
    },

    onEditScannerClick:
      ownProps.onEditScannerClick ||
      (() => {
        // Fallback handler - no action needed
      }),

    onDeleteScannerClick: (scanConfig) => {
      // TODO: Delete scanner action
      dispatch(
        modalActions.openModal({
          modalType: DeleteConfirmModal.modalType,
          modalProps: {
            scanConfig
          }
        })
      );
    },

    onAddScanner:
      ownProps.onAddScanner ||
      (() => {
        // Fallback handler - no action needed
      })
  };
};

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps,
  onRunNowClick: (scanConfig) =>
    dispatchProps.onRunNowClick(stateProps.organizationId, scanConfig),
  onAbortClick: (scanConfig) =>
    dispatchProps.onAbortClick(stateProps.organizationId, scanConfig)
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps, mergeProps, {
    context: RootStoreContext
  })
)(ScanConfigurationsListComponent);
